import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getJWTFromLocalStorage } from '../services/helpers';

const Preferences = () => {
  const navigate = useNavigate();
  const SERVER_URL = `http${process.env.REACT_APP_API_URL}`;

  // State for form fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    username: '',
    notifications: {
      text: true,
      email: true
    }
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const token = await getJWTFromLocalStorage();
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(`${SERVER_URL}/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          navigate('/login');
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        let userData = await response.json();
        if (typeof userData === 'string') {
          try {
            userData = JSON.parse(userData);
          } catch (e) {
            console.error("Error parsing userData string:", e);
          }
        }
        // Also get any stored user data from localStorage as backup
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

        console.log("Fetched user datatype", typeof userData, userData);
        console.log("Fetched user fullname", userData.full_name);
        console.log("Stored user datatype", typeof storedUser, storedUser);

        // Populate the form with available data, prioritizing API response
        setFormData({
          name: userData.full_name || storedUser.full_name || '',
          username: userData.username || storedUser.username || '',
          email: userData.email || storedUser.email || '', // Keep these fields for future implementation
          phone: userData.phone || storedUser.phone || '',
          password: '', // Always empty password field for security
          notifications: {
            text: true,
            email: true
          }
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
        setMessage({
          text: 'Failed to load user data. Please try again.',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate, SERVER_URL]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      notifications: {
        ...formData.notifications,
        [name]: checked
      }
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const token = await getJWTFromLocalStorage();
      if (!token) {
        navigate('/login');
        return;
      }

      // Only send fields that are used in the backend
      const updateData = {
        full_name: formData.name,
        username: formData.username
      };

      // Add password only if it's provided
      // if (formData.password) {
      //   updateData.password = formData.password;
      // }

      const response = await fetch(`${SERVER_URL}/updateProfile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      const result = await response.json();
      console.log("Update result:", result);

      // Update local storage with new user data
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...storedUser,
        full_name: result.full_name,
        username: result.username
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update the form with the latest data
      setFormData({
        ...formData,
        name: result.full_name,
        username: result.username,
        password: '' // Clear password field after successful update
      });

      setMessage({
        text: 'Profile updated successfully!',
        type: 'success'
      });

    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({
        text: error.message || 'Failed to update profile. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Get initials for the avatar
  const getInitials = (name) => {
    if (!name) return "NA";
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle photo upload
  const handlePhotoUpload = () => {
    // This functionality would be implemented in a future update
    alert('Photo upload functionality will be available in a future update.');
  };

  if (isLoading) {
    return (
      <div className="content-container">
        <h1 className="page-title">Settings & Preferences</h1>
        <div className="loading-indicator">Loading your profile information...</div>
      </div>
    );
  }

  return (
    <div className="content-container">
      <h1 className="page-title">Settings & Preferences</h1>

      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24'
        }}>
          {message.text}
        </div>
      )}

      <div className="preferences-container">
        <div className="profile-section">
          <div className="photo-upload-container">
            <div className="profile-photo-placeholder">
              <div className="initials-placeholder">{getInitials(formData.name)}</div>
            </div>
            {/* <button
              className="upload-photo-btn"
              onClick={handlePhotoUpload}
            >
              Upload Photo
            </button> */}
          </div>

          <form className="preferences-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <div className="input-with-icon-wrapper">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-control"
                />
                <i className="fa-solid fa-pen input-icon"></i>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <div className="input-with-icon-wrapper">
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="form-control"
                />
                <i className="fa-solid fa-pen input-icon"></i>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-control"
                disabled
              />
            </div>

            {/* <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-control"
              />
            </div> */}

            {/* <div className="form-group">
              <label htmlFor="password">Password (leave blank to keep current)</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="form-control"
              />
            </div> */}

            {/* <div className="form-group notification-settings">
              <label className="section-label">Notification Settings</label>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="textNotifications"
                  name="text"
                  checked={formData.notifications.text}
                  onChange={handleCheckboxChange}
                  className="checkbox-input"
                />
                <label htmlFor="textNotifications" className="checkbox-label">
                  Text Notifications
                </label>
              </div>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="emailNotifications"
                  name="email"
                  checked={formData.notifications.email}
                  onChange={handleCheckboxChange}
                  className="checkbox-input"
                />
                <label htmlFor="emailNotifications" className="checkbox-label">
                  Email Notifications
                </label>
              </div>
            </div> */}

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Preferences;